"use client"

import React from 'react'
import Image from 'next/image'
import { cn } from '@/shared/utils/tw'
import type { KioskHeaderProps } from '../types/kiosk-config'
import { HEADER_TITLE_TEXT } from '../types/kiosk-config'

// Figma 기준 사이즈 (2160 x 3840)
const FIGMA_WIDTH = 2160
const FIGMA_HEIGHT = 3840

/**
 * 키오스크 헤더 컴포넌트
 *
 * Figma 디자인: node-id=2302-1524
 *
 * 구성 (독립 배치 - flex row 아님):
 * - 좌측 상단: 로고 (x=130, y=150)
 * - 상단 중앙: "AI 민원 도우미" 타이틀 (textAlignHorizontal CENTER)
 *
 * 언어 버튼은 하단 우측으로 이동 → kiosk-avatar-page.tsx에서 직접 렌더링
 */
export const KioskHeader = ({
  onLogoClick,
  title,
  selectedLanguage = 'ko',
}: KioskHeaderProps) => {
  const displayTitle = title ?? HEADER_TITLE_TEXT[selectedLanguage]
  return (
    <>
      {/* 로고 - Figma: x=130, y=150, 179x123 */}
      <button
        type="button"
        onClick={onLogoClick}
        className={cn(
          'absolute z-50',
          'flex items-center justify-center',
          'pointer-events-auto',
          'bg-transparent border-none outline-none cursor-pointer',
          // 터치 영역 확보 (패딩 대신 min-size)
          'min-w-[44px] min-h-[44px]'
        )}
        style={{
          left: `${(130 / FIGMA_WIDTH) * 100}%`,
          // Figma: logo y=150, h=123 → center at 211.5
          top: `${(211.5 / FIGMA_HEIGHT) * 100}%`,
          transform: 'translateY(-50%)',
        }}
      >
        <Image
          src="/svg/gg-logo.svg"
          alt="굳갱랩스"
          width={33}
          height={22}
          priority
          className={cn(
            'w-[8.29cqw]',
            'h-auto',
            'object-contain'
          )}
        />
      </button>

      {/* "AI 민원 도우미" 타이틀 + 시범 운영 뱃지 - Figma: 상단 중앙 */}
      {displayTitle && (
        <div
          className={cn(
            'absolute z-50',
            'left-0 w-full',
            'flex items-center justify-center',
            'pointer-events-none'
          )}
          style={{
            top: `${(211.5 / FIGMA_HEIGHT) * 100}%`,
            transform: 'translateY(-50%)',
            gap: '12px',
          }}
        >
          {/* 시범 운영 뱃지 (Figma: 3151:2573) */}
          <span
            className="flex items-center justify-center font-pretendard font-semibold text-[#c198a3] whitespace-nowrap"
            style={{
              fontSize: '2.22cqw',
              lineHeight: '1.4',
              background: 'white',
              backdropFilter: 'blur(11.1px)',
              borderRadius: '1.11cqw',
              padding: '0.37cqh 0.93cqw',
            }}
          >
            시범 운영
          </span>
          <span
            className={cn(
              'font-pretendard font-semibold text-white',
              'text-[4.44cqw]',
              'leading-none',
              'tracking-[-0.01em]',
              'whitespace-nowrap',
            )}
          >
            {displayTitle}
          </span>
        </div>
      )}
    </>
  )
}

export default KioskHeader
